"use client";

import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Image from "next/image";
import { Check } from "lucide-react";
import { ProcessingStep } from "@/interfaces/customization-form.interface";
import { IMAGE_PROCESSING_STEPS } from "@/constants/image";

interface Props {
  selectProcessingSteps: () => Promise<ProcessingStep[] | undefined>;
}

const ImageContext = createContext<Props>({
  selectProcessingSteps: () => Promise.resolve(undefined),
});

export default function ImageProvider({ children }: { children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState<ProcessingStep[]>();
  const confirmPromise = useRef<(step?: ProcessingStep[]) => void>(null);

  const startSelectSteps = async () => {
    setSteps([]);
    setOpen(true);
    return new Promise<ProcessingStep[] | undefined>((resolve) => {
      confirmPromise.current = resolve;
    });
  };

  const onSelectSteps = (steps?: ProcessingStep[]) => {
    confirmPromise.current?.(steps);
    setOpen(false);
    setSteps([]);
  };

  useEffect(() => {
    if (!open) {
      confirmPromise.current?.(undefined);
    }
  }, [open]);

  return (
    <ImageContext.Provider value={{ selectProcessingSteps: startSelectSteps }}>
      <Dialog
        header="Image processing steps"
        visible={open}
        className="dialog"
        style={{ width: 500 }}
        headerStyle={{ padding: 16 }}
        contentClassName="border-t-1 border-gray-300"
        contentStyle={{ padding: 0 }}
        onHide={() => onSelectSteps(undefined)}
        footer={() => {
          return (
            <div className="flex justify-end gap-4 p-4 pt-0">
              <Button
                severity="secondary"
                style={{
                  height: 36,
                  margin: 0,
                  padding: "6px 10px",
                  gap: 4,
                }}
                label="Cancel"
                onClick={() => onSelectSteps([])}
              />
              <Button
                severity="info"
                style={{
                  height: 36,
                  margin: 0,
                  padding: "6px 10px",
                  gap: 4,
                }}
                label="Select"
                icon={<Check size={18} />}
                disabled={!steps?.length}
                onClick={() => onSelectSteps(steps)}
              />
            </div>
          );
        }}
      >
        <div className="flex h-full overflow-auto p-4">
          {IMAGE_PROCESSING_STEPS.map((s) => {
            const isSelected = steps?.find((_s) => _s.type === s.type);
            return (
              <div
                key={s.type}
                className="flex flex-col items-start p-2 justify-start gap-2 cursor-pointer"
                onClick={() => {
                  const newSteps = isSelected
                    ? steps?.filter((_s) => _s.type !== s.type)
                    : [...(steps || []), s];
                  setSteps(newSteps);
                }}
              >
                <Image
                  className="rounded overflow-hidden border border-primary-50 object-cover"
                  alt={s.label}
                  width={100}
                  height={100}
                  src={s.thumbnail}
                  style={{
                    borderWidth: isSelected ? 2 : 1,
                    minWidth: 100,
                    minHeight: 100,
                    maxWidth: 100,
                    maxHeight: 100,
                  }}
                />
                <span className="text-sm font-bold">{s.label}</span>
              </div>
            );
          })}
        </div>
      </Dialog>

      {children}
    </ImageContext.Provider>
  );
}

export const useImage = () => useContext(ImageContext);
